import React, { useContext, useState } from "react";
import styled from "styled-components";
import { Form, Container } from "semantic-ui-react";
import * as yup from "yup";
import { useFormik } from "formik";
import { EMAIL_USERNAME_VERIFICATION } from "../../../helpers/graphqlQueries";
import { Context } from "../../../context/globalContext";
import { toast } from "react-toastify";
import { routes } from "../../../helpers/routes";
import firebase from "firebase/app";
import "firebase/auth";
import { trackEvent, trackIdentity } from "../../../helpers/analytics";
import { sendWelcomeEmail } from "../services";
import { CREATE_USER } from "../../../helpers/graphqlMutations";

const CompleteUserSchema = yup.object().shape({
  username: yup
    .string()
    .trim()
    .matches(
      /^[a-zA-Z0-9_.-]+$/,
      "Use only letters, numbers, dots, hyphens and underscores"
    )
    .required("Username is a required field"),
});

export default function CompleteUserData() {
  const { graphQLClient } = useContext(Context);
  const [loading, setLoading] = useState(false);

  const {
    errors,
    handleChange,
    handleSubmit,
    values,
    setFieldValue,
    touched,
    setFieldTouched,
  } = useFormik({
    initialValues: {
      username: "",
    },
    validationSchema: CompleteUserSchema,
    onSubmit: onSubmitHandle,
  });

  async function onSubmitHandle(data) {
    const user = firebase.auth().currentUser;
    const resultVerify = await verifyEmailAndUsername(data.username);
    if (!resultVerify) {
      toast.error("Username is already used");
      setLoading(false);
      return;
    }
    setLoading(true);
    const variables = {
      username: data.username.trim().toLowerCase(),
      uid: user.uid,
      email: user.email,
    };
    await graphQLClient
      .mutate({
        variables,
        mutation: CREATE_USER,
      })
      .then((result) => {
        sendWelcomeEmail(variables)
        console.log(result);
      });
    trackIdentity(data.email, {
      username: data.username.trim().toLowerCase(),
    });
    trackEvent("Account created", {
      email: data.email,
      username: data.username.trim().toLowerCase(),
    });
    window.location.replace(routes.SIGNIN);
  }

  async function verifyEmailAndUsername(username) {
    const emailAndUsername = await graphQLClient.query({
      variables: { email: "", username },
      query: EMAIL_USERNAME_VERIFICATION,
    });
    if (emailAndUsername?.data?.user_account?.length) return false;
    return true;
  }

  return (
    <ContainerWrapper>
      <FormWrapper onSubmit={handleSubmit}>
        <Form.Field required>
          <Label>Username</Label>
          <ExtraLabel>
            Your username will be displayed on your public profile and reviews.
            It can not be changed later
          </ExtraLabel>
          <input
            onChange={(e) =>
              setFieldValue("username", e.target.value.replace("@", ""))
            }
            onBlur={() => setFieldTouched("username", true)}
            value={values.username}
            type='text'
            name='username'
          />
          {errors.username && touched.username && (
            <Error>{errors.username}</Error>
          )}
        </Form.Field>
        <Form.Button
          type='submit'
          content='Complete data'
          fluid
          size='huge'
          active={false}
          loading={loading}
          primary={
            (!Object.keys(errors).length &&
              Object.keys(touched).length !== 0) ||
            false
          }
        />
      </FormWrapper>
    </ContainerWrapper>
  );
}

const ContainerWrapper = styled(Container)`
  box-shadow: 0px 0px 2px 0px rgba(0, 0, 0, 0.1),
    0px 1px 4px 0px rgba(0, 0, 0, 0.1);
  background-color: #fff;
  margin: 80px 0;
  padding: 20px 0px;
`;

const FormWrapper = styled(Form)`
  margin: 32px auto;
  width: 45%;
  @media (max-width: 991px) {
    width: 60%;
  }
  @media (max-width: 768px) {
    width: 80%;
  }
`;
const Label = styled.label`
  font-size: 1.34em !important;
`;
const ExtraLabel = styled.label`
  font-size: 0.8em !important;
`;
const Error = styled.span`
  color: #f00;
`;
